/**
 * ============================================================================
 * USE USER HOOK
 * ============================================================================
 * 
 * Custom hook for user-related operations
 */

import { useState, useEffect, useCallback } from 'react';
import * as userService from '../services/users';
import useMounted from './useMounted';
import useAbortController from './useAbortController';

export const useUser = (userId = null) => {
  const [user, setUser] = useState(null);
  // When userId is null we're in "current user" mode; no auto-fetch so start with loading false
  const [loading, setLoading] = useState(!!userId);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [givenRatings, setGivenRatings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const isMounted = useMounted();
  const { signal, abort } = useAbortController();

  useEffect(() => {
    if (userId) {
      loadUser(userId);
    }
    return () => {
      abort(); // Cancel pending requests on unmount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  /**
   * Load user profile by ID
   */
  const loadUser = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getUserProfile(id);
      
      // Only update state if component is still mounted
      if (isMounted()) {
        setUser(response.data);
      }
    } catch (err) {
      // Don't update state if component unmounted or request aborted
      if (!isMounted() || err.name === 'AbortError') {
        return;
      }
      if (isMounted()) {
        setError(err.message);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted]);

  /**
   * Load current user profile
   */
  const loadMyProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getMyProfile();
      
      // Only update state if component is still mounted
      if (isMounted()) {
        setUser(response.data);
      }
    } catch (err) {
      // Don't update state if component unmounted or request aborted
      if (!isMounted() || err.name === 'AbortError') {
        return;
      }
      if (isMounted()) {
        setError(err.message);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted]);

  /**
   * Update profile (with optimistic update)
   */
  const updateProfile = useCallback(async (data) => {
    // Optimistic update
    const previousUser = user;
    const optimisticUser = { ...user, ...data };
    setUser(optimisticUser);

    try {
      setLoading(true);
      setError(null);
      const response = await userService.updateProfile(data);
      
      if (isMounted()) {
        setUser(response.data);
      }
      return response.data;
    } catch (err) {
      // Rollback on error
      if (isMounted()) {
        setUser(previousUser);
        setError(err.message);
      }
      throw err;
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [user, isMounted]);

  /**
   * Upload profile photo
   */
  const uploadPhoto = useCallback(async (file) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.uploadProfilePhoto(file);
      await loadMyProfile(); // Reload profile
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadMyProfile]);

  /**
   * Delete profile photo
   */
  const deletePhoto = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await userService.deleteProfilePhoto();
      await loadMyProfile(); // Reload profile
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadMyProfile]);

  /**
   * Get user statistics
   */
  const loadStats = useCallback(async (id = userId) => {
    try {
      setLoading(true);
      setError(null);
      const response = id
        ? await userService.getUserStats(id)
        : await userService.getMyStats();
      const statsData = response?.data?.stats ?? response?.data?.data?.stats ?? response?.data;
      setStats(statsData);
      return statsData;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Get user activity
   */
  const loadActivity = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getMyActivity(filters);
      setActivity(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get user transactions
   */
  const loadTransactions = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getMyTransactions(filters);
      setTransactions(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get user ratings
   */
  const loadRatings = useCallback(async (id = userId, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = id
        ? await userService.getUserRatings(id, filters)
        : await userService.getUserRatings(null, filters);
      setRatings(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Get given ratings
   */
  const loadGivenRatings = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getMyGivenRatings(filters);
      setGivenRatings(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Submit rating
   */
  const submitRating = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.submitRating(data);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request KYC verification
   */
  const requestKYC = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.requestKYCVerification(data);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get verification status
   */
  const loadVerificationStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getVerificationStatus();
      setVerificationStatus(response.data);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Search users
   */
  const searchUsers = useCallback(async (query, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.searchUsers(query, filters);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get settings
   */
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getSettings();
      setSettings(response.data);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update settings
   */
  const updateSettings = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.updateSettings(data);
      setSettings(response.data);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Deactivate account
   */
  const deactivateAccount = useCallback(async (reason) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.deactivateAccount({ reason });
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request account deletion
   */
  const requestAccountDeletion = useCallback(async (reason) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.requestAccountDeletion({ reason });
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cancel account deletion
   */
  const cancelAccountDeletion = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.cancelAccountDeletion();
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Export user data (GDPR)
   */
  const exportUserData = useCallback(async (format = 'json') => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.exportUserData({ format });
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete account (GDPR)
   */
  const deleteAccount = useCallback(async (password) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.deleteAccount({ password });
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    user,
    stats,
    activity,
    transactions,
    ratings,
    givenRatings,
    settings,
    verificationStatus,
    loading,
    error,
    
    // Actions
    loadUser,
    loadMyProfile,
    updateProfile,
    uploadPhoto,
    deletePhoto,
    loadStats,
    loadActivity,
    loadTransactions,
    loadRatings,
    loadGivenRatings,
    submitRating,
    requestKYC,
    loadVerificationStatus,
    searchUsers,
    loadSettings,
    updateSettings,
    deactivateAccount,
    requestAccountDeletion,
    cancelAccountDeletion,
    exportUserData,
    deleteAccount,
    
    // Convenience
    refresh: userId ? () => loadUser(userId) : loadMyProfile,
  };
};

export default useUser;
